import { User } from "@auth0/auth0-react";
import { sortBy } from "lodash";
import { checkPermission, toCategoryName } from "@/utils";
import { InsightResult, MindsetResult, TabOption } from "@/types";



export const getInsightScores = (tab: TabOption, insight: InsightResult[], mindsets: MindsetResult[]) => {
  switch (tab.name) {
    case "demographics":
      return insight.filter(x => x.tag_type === 'Demographics');
    case "interests":
      return insight.filter(x => x.tag_type === 'Interests');
    case "causes":
      return insight.filter(x => x.tag_type === 'Causes');
    case "spend":
      return insight.filter(x => ["Spend Categories", "Spend Merchants"].includes(x.tag_type));
    case "media":
      return insight.filter(x => (x.tag_type === 'Media') || (x.tag_type === 'Spend Merchants' && x.tag_category === 'Media Subscriptions'));
    case "attitudes":
      return insight.filter(x => x.tag_type === 'Attitudes');
    case "digital":
      return insight.filter(x => x.tag_type === 'Digital Consumption');
    case "statements":
      return insight.filter(x => x.tag_type === 'Lifestyle Statements');
    case "movement":
      return insight.filter(x => x.tag_type === 'Movement');
    case "mindsets":
      return mindsets;
    case "top scoring":
      return insight
    default:
      return []
  }
}


export const filterTabs = (tabs: TabOption[], user: User) => {

  const client = process.env.REACT_APP_CLIENT || 'core';
  let filteredTabs = tabs;

  // Filter by permission
  filteredTabs = filteredTabs.filter( x => (!x.permission) || (x.permission && checkPermission(user, x.permission)));

  // Filter by client
  filteredTabs = filteredTabs.filter( x => x.client === undefined || x.client.includes(client))

  return filteredTabs;

}

export const filterLevels = <T extends { tag_level: number }>(
  scores: T[],
  levels: number[],
): T[] => {
  return scores.filter((x) => levels.includes(x.tag_level));
};

export const filterCategories = <
  T extends { tag_name: string; tag_category: string },
>(
  scores: T[],
  tag_name: string,
  tag_category: string,
  sort: string,
) => {
  // Create a copy
  let filteredScores = scores;

  // Filter by tag category
  filteredScores = filteredScores.filter(
    (x) => x.tag_category === toCategoryName(tag_category),
  );

  // Filter by tag name
  filteredScores = filteredScores.filter((x) => x.tag_name !== tag_name);

  // Order the scores
  filteredScores = sortBy(filteredScores, sort).reverse();

  return filteredScores;
};

export const filterBrands = <
  T extends {
    tag_group: string;
    tag_category: string;
    tag_name: string;
    tag_level: number;
  },
>(
  scores: T[],
  tag_group?: string,
  tag_category?: string,
  levels?: number[],
  searchText?: string,
  sort?: string,
  num?: number,
) => {
  // Create a copy
  let filteredScores = scores;

  // Filter by tag group
  if (tag_group) {
    filteredScores = filteredScores.filter(
      (x) => x.tag_group === toCategoryName(tag_group),
    );
  }

  // Filter by tag category
  if (tag_category) {
    filteredScores = filteredScores.filter(
      (x) => x.tag_category === toCategoryName(tag_category),
    );
  }

  // Filter by levels
  if (levels) {
    filteredScores = filterLevels(filteredScores, levels);
  }

  // Filter by name
  if (searchText) {
    filteredScores = filteredScores.filter((x) =>
      x.tag_name.toLowerCase().includes(searchText.toLowerCase()),
    );
  }

  // Order the scores
  if (sort) {
    filteredScores = sortBy(filteredScores, sort).reverse();
  }

  // Limit the number of results
  if (num) {
    filteredScores = filteredScores.slice(0, num);
  }

  return filteredScores;
};

export const filterItems = <
  T extends { tag_name: string; tag_category: string },
>(
  scores: T[],
  sort: string,
) => {
  // Create a copy
  let filteredScores = scores;

  // Order the scores
  filteredScores = sortBy(filteredScores, sort).reverse();

  return filteredScores;
};


export const filterScores = <
  T extends { tag_name: string; tag_group: string },
>(
  scores: T[],
  tag_group: string,
  sort: string,
) => {
  // Create a copy
  let filteredScores = scores;

  // Filter by tag group
  filteredScores = filteredScores.filter(
    (x) => x.tag_group === tag_group,
  );

  // Order the scores
  filteredScores = sortBy(filteredScores, sort).reverse();

  return filteredScores;
};

export const searchScores = <
  T extends { tag_name: string, tag_category: string },
>(
  scores: T[],
  searchText: string,
  sort: string,
) => {
  // Create a copy
  let filteredScores = scores;

  // Search the tag name
  filteredScores = filteredScores.filter((x) =>
    (x.tag_name.toLowerCase().includes(searchText.toLowerCase()))
    || (x.tag_category.toLowerCase().includes(searchText.toLowerCase()))
  );

  // Order the scores
  filteredScores = sortBy(filteredScores, sort).reverse();

  return filteredScores;
};

export const calculateStatementScore = (statement: string, statementData: InsightResult[]) => {

  const agree = statementData.find(x => x.tag_name === 'Agree');
  const neither = statementData.find(x => x.tag_name === 'Neither Agree nor Disagree');
  const disagree = statementData.find(x => x.tag_name === 'Disagree');

  const score = (((agree?.score || 0) * 2) / ((agree?.score || 0) + (disagree?.score || 0)));
  const score_rgn = (((agree?.score_rgn || 0) * 2) / ((agree?.score_rgn || 0) + (disagree?.score_rgn || 0)));
  const rate = (agree?.rate || 0);
  const rates = [(disagree?.rate || 0), (neither?.rate || 0), (agree?.rate || 0)];

  return (
    {
      name: statement,
      score_wt: score,
      score_rgn: score_rgn,
      rate: rate,
      rates: rates
    }
  )
}

export const compareStat = (a: number, b: number) => {
  return a && b && (b - a) / a
}
